import {
  type StepType,
  TourProvider as ReactourProvider,
  useTour,
} from '@reactour/tour'
import React, { useEffect, useState } from 'react'

import { TourConfig } from '../types/tour'
import { TourModal } from './tour-modal'

const TOUR_STORAGE_KEY = '@app/tour-history'

type TourHistoryEntry = {
  completed?: boolean
  initialTourSeen?: boolean
  lastSeen: string
  dontShowAgain?: boolean
  completedSteps: string[]
}

export interface TourHistory {
  [version: string]: TourHistoryEntry
}

const TourManager = ({
  children,
  version,
}: {
  children: React.ReactNode
  version: string
}) => {
  const [showModal, setShowModal] = useState(false)
  const { setIsOpen } = useTour()

  useEffect(() => {
    const checkTourStatus = () => {
      try {
        const tourHistory = localStorage.getItem(TOUR_STORAGE_KEY)
        const parsedHistory: TourHistory = tourHistory
          ? JSON.parse(tourHistory)
          : {}

        const previousVersion = Object.keys(parsedHistory)
          .filter((v) => v !== version)
          .sort()
          .pop()

        if (previousVersion && version !== previousVersion) {
          setShowModal(true)
          return
        }

        const currentTourData = parsedHistory[version]
        if (
          !currentTourData ||
          (!currentTourData.completed && !currentTourData.dontShowAgain)
        ) {
          setShowModal(true)
        }
      } catch (error) {
        console.error('Erro ao verificar status do tour:', error)
      }
    }

    checkTourStatus()
  }, [version])

  const updateTourHistory = (dontShowAgain?: boolean) => {
    try {
      const tourHistory = localStorage.getItem(TOUR_STORAGE_KEY)
      const parsedHistory: TourHistory = tourHistory
        ? JSON.parse(tourHistory)
        : {}

      parsedHistory[version] = {
        ...parsedHistory[version],
        initialTourSeen: true,
        lastSeen: new Date().toISOString(),
        dontShowAgain,
        completedSteps: parsedHistory[version]?.completedSteps || [],
        completed: true,
      }

      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(parsedHistory))
    } catch (error) {
      console.error('Erro ao salvar status do tour:', error)
    }
  }

  const handleStartTour = () => {
    setShowModal(false)
    setTimeout(() => {
      setIsOpen(true)
      updateTourHistory(false)
    }, 100)
  }

  const handleSkipTour = (dontShowAgain: boolean = false) => {
    setShowModal(false)
    updateTourHistory(dontShowAgain)
  }

  return (
    <>
      <TourModal
        open={showModal}
        setOpen={setShowModal}
        onStartTour={handleStartTour}
        onSkipTour={handleSkipTour}
        updateTourHistory={updateTourHistory}
      />
      {children}
    </>
  )
}

type Props = {
  children: React.ReactNode
  config: TourConfig
}

export const TourProvider: React.FC<Props> = ({ children, config }) => {
  const { initialSteps, version } = config
  const tourSteps: StepType[] = initialSteps.map((step) => ({
    selector: step.selector,
    content: step.content,
    position: step.position,
    action: step.action,
  }))

  return (
    <ReactourProvider
      steps={tourSteps}
      showBadge={false}
      showCloseButton={false}
      showDots={false}
      className="rounded-b2b shadow-md p-6"
    >
      <TourManager version={version}>{children}</TourManager>
    </ReactourProvider>
  )
}
